import { useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { type AdminFeedbackSummary } from "@/api/feedback-api"
import { type Lane } from "@/components/feedback-dialog"
import {
  AlertOctagon,
  Bug,
  Loader2,
  MessageSquare,
  MessageSquareHeart,
  RefreshCw,
  ShieldAlert,
  ThumbsDown,
  ThumbsUp,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { useAdminFeedbackListQuery } from "../../hooks/admin-feedback-queries"
import { CollapsibleManagementSection } from "../collapsible-management-section"
import { FeedbackAnalytics } from "./feedback-analytics"
import { FeedbackDetailDialog } from "./feedback-detail-dialog"

const LANE_FILTERS: Array<{ value: Lane | "all"; labelKey: string; fallback: string }> = [
  { value: "all", labelKey: "adminDashboard.feedback.filter.all", fallback: "All" },
  {
    value: "response_feedback",
    labelKey: "adminDashboard.feedback.filter.responseFeedback",
    fallback: "Answer feedback",
  },
  { value: "bug", labelKey: "adminDashboard.feedback.filter.bug", fallback: "Bugs" },
  { value: "incident", labelKey: "adminDashboard.feedback.filter.incident", fallback: "Incidents" },
]

export function FeedbackSection() {
  const { t } = useTranslation()
  const [laneFilter, setLaneFilter] = useState<Lane | "all">("all")
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const list = useAdminFeedbackListQuery({
    lane: laneFilter === "all" ? null : laneFilter,
    limit: 200,
  })

  // Refresh invalidates every query under the feedback scope so the
  // analytics panels, the records list, and any open detail drawer all
  // re-fetch together. Otherwise the "Refresh" button feels partial.
  const handleRefresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "feedback"] })
  }

  const headerActions = (
    <Button size="sm" variant="outline" onClick={handleRefresh}>
      <RefreshCw className={cn("mr-2 size-4", list.isFetching && "animate-spin")} />
      {t("common.refresh", "Refresh")}
    </Button>
  )

  return (
    <CollapsibleManagementSection
      headerActions={headerActions}
      icon={<MessageSquare className="size-5" />}
      id="feedback"
      storageKey="admin-management-feedback-expanded"
      title={t("adminDashboard.feedback.title", "Feedback")}
    >
      <div className="space-y-6">
        <FeedbackAnalytics />

        <div className="flex flex-wrap items-center gap-2">
          {LANE_FILTERS.map((opt) => {
            const active = laneFilter === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                className={cn(
                  "rounded-full border px-3 py-1 text-xs transition-colors",
                  active
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground hover:bg-accent/40"
                )}
                onClick={() => setLaneFilter(opt.value)}
              >
                {t(opt.labelKey, opt.fallback)}
              </button>
            )
          })}
        </div>

        <ListBody
          error={list.error}
          isLoading={list.isLoading}
          items={list.data?.items}
          onSelect={setSelectedId}
        />
      </div>

      <FeedbackDetailDialog
        id={selectedId}
        open={selectedId !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null)
        }}
      />
    </CollapsibleManagementSection>
  )
}

function ListBody({
  isLoading,
  error,
  items,
  onSelect,
}: {
  isLoading: boolean
  error: unknown
  items: AdminFeedbackSummary[] | undefined
  onSelect: (id: string) => void
}) {
  const { t } = useTranslation()
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      </div>
    )
  }
  if (error) {
    return (
      <div className="text-destructive py-8 text-center text-sm">
        {t("adminDashboard.feedback.loadError", "Could not load feedback records.")}
      </div>
    )
  }
  if (!items || items.length === 0) {
    return (
      <div className="text-muted-foreground py-8 text-center text-sm">
        {t("adminDashboard.feedback.empty", "No feedback records yet.")}
      </div>
    )
  }
  return <FeedbackList items={items} onSelect={onSelect} />
}

// -----------------------------------------------------------------------------
// List
// -----------------------------------------------------------------------------

function FeedbackList({
  items,
  onSelect,
}: {
  items: AdminFeedbackSummary[]
  onSelect: (id: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="border-border divide-border divide-y rounded-md border">
      {items.map((item) => (
        <button
          key={item.id}
          className="hover:bg-accent/30 flex w-full items-start gap-3 p-3 text-left transition-colors"
          type="button"
          onClick={() => onSelect(item.id)}
        >
          <LaneIcon lane={item.lane} preview={item.preview} />
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">
                {t(`adminDashboard.feedback.lane.${item.lane ?? "unknown"}`, laneLabel(item.lane))}
              </span>
              {item.escalated && (
                <Badge className="gap-1" variant="default">
                  <ShieldAlert className="size-3" />
                  {t("adminDashboard.feedback.escalated", "Escalated")}
                </Badge>
              )}
              <span className="text-muted-foreground text-xs">
                {item.user.name ?? item.user.id ?? "—"}
              </span>
              <span className="text-muted-foreground ml-auto text-xs">
                {formatTimestamp(item.submitted_at)}
              </span>
            </div>
            <PreviewLine item={item} />
          </div>
        </button>
      ))}
    </div>
  )
}

function LaneIcon({
  lane,
  preview,
}: {
  lane: Lane | null
  preview: AdminFeedbackSummary["preview"]
}) {
  if (lane === "response_feedback") {
    const sentiment =
      preview && "sentiment" in preview ? (preview as { sentiment?: string }).sentiment : undefined
    return sentiment === "up" ? (
      <ThumbsUp className="text-primary mt-0.5 size-5 shrink-0" />
    ) : (
      <ThumbsDown className="text-muted-foreground mt-0.5 size-5 shrink-0" />
    )
  }
  if (lane === "bug") return <Bug className="text-muted-foreground mt-0.5 size-5 shrink-0" />
  if (lane === "incident") return <AlertOctagon className="text-primary mt-0.5 size-5 shrink-0" />
  return <MessageSquareHeart className="text-muted-foreground mt-0.5 size-5 shrink-0" />
}

function PreviewLine({ item }: { item: AdminFeedbackSummary }) {
  const { t } = useTranslation()
  if (!item.preview) {
    return (
      <p className="text-muted-foreground text-xs italic">
        {t("adminDashboard.feedback.noPreview", "No preview available.")}
      </p>
    )
  }
  if (item.lane === "response_feedback") {
    const p = item.preview as { sentiment?: string; comment?: string | null }
    return (
      <p className="text-muted-foreground line-clamp-2 text-xs">
        {p.comment ?? t("adminDashboard.feedback.noComment", "No comment.")}
      </p>
    )
  }
  if (item.lane === "bug") {
    const p = item.preview as {
      severity?: string
      whoAffected?: string
      whatTrying?: string | null
    }
    return (
      <p className="text-muted-foreground line-clamp-2 text-xs">
        <span className="text-foreground font-medium">
          {p.severity
            ? t(`feedback.bug.severity.${p.severity}`, { defaultValue: p.severity })
            : "—"}
        </span>{" "}
        ·{" "}
        {p.whoAffected
          ? t(`feedback.bug.whoAffected.${p.whoAffected}`, { defaultValue: p.whoAffected })
          : "—"}{" "}
        · {p.whatTrying ?? "—"}
      </p>
    )
  }
  if (item.lane === "incident") {
    const p = item.preview as { summary?: string | null; timeframe?: string | null }
    return (
      <p className="text-muted-foreground line-clamp-2 text-xs">
        {p.summary ?? "—"}
        {p.timeframe ? ` · ${p.timeframe}` : ""}
      </p>
    )
  }
  return null
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function laneLabel(lane: Lane | null): string {
  switch (lane) {
    case "response_feedback":
      return "Answer feedback"
    case "bug":
      return "Bug"
    case "incident":
      return "Incident"
    default:
      return "Unknown"
  }
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return "—"
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}
